import React, {
  useEffect,
  useRef
} from 'react';

import * as echarts from 'echarts';
import { EChartsType } from 'echarts';
import { worldMapConfig } from './config';
import { regionData, regionType } from './type';
import { useIntl, getLocale } from 'umi';
import { Icon } from 'tea-component';
import axios from 'axios';
import { reviewsFormat } from '../../utils/value';
import BubbleCommon, { TipsMapType } from '../BubbleCommon';
import './index.scss';

export default function Map({
  data,
  max,
  total,
  regionType,
  unidentifiedMentions,
  height,
  keyMentions,
  handleOnClick,
  bubbleTips,
}: {
  data: regionData;
  max: number;
  total: number;
  regionType: regionType;
  unidentifiedMentions: number;
  height?: number;
  keyMentions?: string;
  handleOnClick?: (e: Event) => void;
  bubbleTips?: TipsMapType;
}) {
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  // 使用 ref 存储 mapInstance，确保在组件生命周期内保持唯一
  const mapInstanceRef = useRef<EChartsType | null>(null);
  // 用于追踪组件是否已挂载
  const isMountedRef = useRef(true);

  const [world3Data, setWorld3Data] = React.useState(null);

  function goRenderMap(world3Data: any) {
    try {
      if (!isMountedRef.current || !ref.current) return;

      if (world3Data) {
        echarts.registerMap('world', { geoJSON: world3Data }); // 注册地图
        renderMap();
        // console.log('world init success', world3Data);
      }
    } catch (error) {
      console.log('world init error', error);
    }
  }

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // 组件卸载时销毁 ECharts 实例
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.dispose();
          mapInstanceRef.current = null;
        } catch (e) {
          // 忽略卸载时的错误
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;

    if (world3Data) {
      goRenderMap(world3Data);
    } else {
      // world3GeoJson  from: https://github.com/honkerzhou/resource-library/tree/master/data-visualization/echarts/map-world
      // import(/* webpackChunkName: "world3" */ './world3').then(
      //   ({ world3GeoJson }) => {
      //     setGeoJson(world3GeoJson);
      //     echarts.registerMap('world', { geoJSON: world3GeoJson }); // 注册地图
      //     renderMap();
      //   },
      // );

      axios
        .get('https://ogdb-cdn.intlgame.cn/front/static/world.json')
        .then((res) => {
          // 检查组件是否仍然挂载
          if (!isMountedRef.current) return;

          if (res?.status === 200 && res?.data) {
            // console.log('world get', res);
            setWorld3Data(res?.data);
            goRenderMap(res?.data);
          }
        })
        .catch((error) => {
          console.log('world network error', error);
        });
    }
  }, [data]);

  const renderMap = () => {
    if (!isMountedRef.current || !ref.current) return;

    const renderedMapInstance = echarts.getInstanceByDom(ref.current);
    if (renderedMapInstance) {
      mapInstanceRef.current = renderedMapInstance;
    } else {
      mapInstanceRef.current = echarts.init(ref.current);
    }

    mapInstanceRef.current?.setOption(
      worldMapConfig({
        data: data,
        mentionsMax: max,
        mentionsTotal: total,
        regionType: regionType,
        unidentifiedMentions: unidentifiedMentions,
        formatMessage,
        getLocale,
        keyMentions: keyMentions,
      }),
      true,
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (isMountedRef.current) {
        mapInstanceRef.current?.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.on('click', (e: Event) => handleOnClick?.(e));
    }
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off('click');
      }
    };
  }, [handleOnClick]);

  // 地图缩放 flag：0 放大  1缩小
  const roamMap = (flag: 0 | 1) => {
    if (!isMountedRef.current || !ref.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = echarts.init(ref.current);
    }

    const currentZoom = mapInstanceRef.current?.getOption()?.geo?.[0]?.zoom;
    if (typeof currentZoom === 'number') {
      mapInstanceRef.current?.setOption({
        geo: {
          zoom: !flag
            ? currentZoom + 0.4 > 5
              ? 5
              : currentZoom + 0.4
            : currentZoom - 0.4 < 1
            ? 1
            : currentZoom - 0.4,
        },
      });
    }
  };

  const titleText = () => {
    let text =
      regionType === 'region'
        ? 'Unidentified Mentions -1'
        : 'Unidentified Mentions -2';

    if (keyMentions === 'News Mentions') {
      text = 'Unidentified News Mentions';
    }

    return text || 'Unidentified';
  };

  return (
    <div className="worldMap">
      <div className="world-map-tips">
        <BubbleCommon tipsMap={bubbleTips} showIcon={false}>
          {formatMessage({ id: titleText() })} :{' '}
          {reviewsFormat(unidentifiedMentions ?? 0, 2, '0')}
        </BubbleCommon>
      </div>
      <div style={{ width: '100%', height: height || '320px' }} ref={ref} />
      <div className="control">
        <span className="control-icon" onClick={() => roamMap(0)}>
          <Icon type="plus" />
        </span>
        <span className="control-icon" onClick={() => roamMap(1)}>
          <Icon type="minus" />
        </span>
      </div>
    </div>
  );
}
